import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { decimal } from 'Helpers/currencyFormat';

import Button from 'CommonComponents/Button';

const SUMMARISE_THRESHOLD = 10;

/**
 * QuantityImpactPreviewDialog
 *
 * Shows a downstream impact preview when a revenue line that has linked
 * expenses is about to be changed.
 *
 * Lifecycle:
 *   1. Revenue edit form submit is intercepted by inline JS in the Blade view.
 *   2. JS dispatches `quantityimpact:check` with { previewUrl, newValue, formEl,
 *      dependentsUrl (optional), sourceLabel (optional) }.
 *   3. This component fetches the impact preview and, if dependents exist,
 *      shows the modal.
 *   4. User confirms → the original form is submitted programmatically.
 *   5. User cancels → form submission is blocked; the modal closes.
 *   6. "View Dependent Lines" → dispatches `dependentlines:open` to open the
 *      DependentLinesPanel slide-out without closing this dialog.
 */
const QuantityImpactPreviewDialog = () => {
    const [state, setState] = useState(null); // null = hidden
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const activeRef = useRef(false);

    const visibleDependents = useMemo(() => {
        if (!preview?.dependents) return [];
        if (expanded || preview.dependents.length <= SUMMARISE_THRESHOLD) return preview.dependents;
        return preview.dependents.slice(0, SUMMARISE_THRESHOLD);
    }, [preview, expanded]);

    const lockForm = useCallback((formEl) => {
        if (!formEl || formEl._submitLocked) return;
        formEl._originalSubmit = HTMLFormElement.prototype.submit.bind(formEl);
        formEl.submit = function () {};
        formEl._submitLocked = true;
    }, []);

    const unlockForm = useCallback((formEl, reEnableButtons = false) => {
        if (!formEl || !formEl._submitLocked) return;
        formEl.submit = formEl._originalSubmit;
        delete formEl._originalSubmit;
        delete formEl._submitLocked;
        if (reEnableButtons) {
            formEl.querySelectorAll('button[type="submit"]').forEach((btn) => {
                btn.disabled = false;
            });
        }
    }, []);

    const handleCheck = useCallback((e) => {
        if (activeRef.current) return;

        const { previewUrl, newValue, formEl, onConfirm, dependentsUrl, sourceLabel } = e.detail || {};
        if (!previewUrl || newValue === undefined || (!formEl && !onConfirm)) return;

        const derivedDependentsUrl = dependentsUrl ?? previewUrl.replace(/-impact-preview\b/, '-dependents');

        const pending = {
            previewUrl,
            newValue,
            formEl: formEl ?? null,
            onConfirm: onConfirm ?? null,
            dependentsUrl: derivedDependentsUrl,
            sourceLabel: sourceLabel ?? null,
        };
        activeRef.current = true;
        lockForm(pending.formEl);
        setError(null);
        setPreview(null);

        axios
            .get(previewUrl, { params: { new_value: newValue } })
            .then(({ data }) => {
                if (!data.dependents || data.dependents.length === 0) {
                    activeRef.current = false;
                    unlockForm(pending.formEl);
                    if (pending.formEl) pending.formEl.submit();
                    else if (pending.onConfirm) pending.onConfirm();
                    return;
                }
                setState(pending);
                setPreview(data);
            })
            .catch(() => {
                setState(pending);
                setError($t('Failed to load impact preview. Do you want to proceed anyway?'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        window.addEventListener('quantityimpact:check', handleCheck);
        return () => window.removeEventListener('quantityimpact:check', handleCheck);
    }, [handleCheck]);

    const handleConfirm = () => {
        const { formEl, onConfirm } = state || {};
        activeRef.current = false;
        unlockForm(formEl);
        setState(null);
        setPreview(null);
        if (formEl) formEl.submit();
        else if (onConfirm) onConfirm();
    };

    const handleCancel = () => {
        activeRef.current = false;
        unlockForm(state?.formEl, true);
        setState(null);
        setPreview(null);
        setError(null);
        setExpanded(false);
    };

    if (!state) return null;

    const formatCurrency = (value) => (value !== null && value !== undefined ? decimal(value, 2) : '—');

    const formatDelta = (value) => {
        if (value === null || value === undefined) return '—';
        const sign = value > 0 ? '+' : '';
        return `${sign}${formatCurrency(value)}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '—';
        const sign = value > 0 ? '+' : '';
        return `${sign}${value}%`;
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
            <div className="tw-bg-white tw-rounded-lg tw-shadow tw-w-full tw-max-w-4xl tw-max-h-[85vh] tw-flex tw-flex-col">
                {/* Header */}
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-x-4 tw-p-4 tw-border-b">
                    <h3 className="tw-text-2xl tw-font-semibold tw-text-blue">{$t('Downstream Impact Preview')}</h3>
                    <svg
                        onClick={handleCancel}
                        className="tw-w-4 tw-h-4 tw-text-charcoal-75 hover:tw-text-charcoal tw-cursor-pointer tw-flex-shrink-0 tw-self-start tw-mt-1"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 14"
                    >
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                    </svg>
                </div>

                {/* Body */}
                <div className="tw-overflow-y-auto tw-flex-1 tw-p-4">
                    {loading && <div className="tw-text-center tw-text-sm tw-text-charcoal-50 tw-py-8">{$t('Loading impact preview...')}</div>}

                    {error && (
                        <div className="tw-space-y-3">
                            <p className="tw-text-sm tw-text-red-600">{error}</p>
                        </div>
                    )}

                    {!loading && preview && (
                        <>
                            {/* Source change summary */}
                            <div className="tw-bg-blue-50 tw-rounded-lg tw-p-3 tw-mb-4">
                                <p className="tw-text-sm tw-text-blue-800">
                                    {$t('Changing')} <strong>{preview.source.label}</strong> {$t('from')}{' '}
                                    <strong>{formatCurrency(preview.source.old_value)}</strong> {$t('to')}{' '}
                                    <strong>{formatCurrency(preview.source.new_value)}</strong> {$t('will affect')}{' '}
                                    <strong>{preview.summary.total_dependents}</strong> {$t('linked budget line(s).')}
                                    {preview.summary.total_dependents > SUMMARISE_THRESHOLD && preview.summary.total_delta !== undefined && (
                                        <>
                                            {' '}
                                            {$t('Total forecast change')}: <strong>{formatDelta(preview.summary.total_delta)}</strong>
                                        </>
                                    )}
                                </p>
                            </div>

                            {preview.summary.any_over_committed && (
                                <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3 tw-mb-4">
                                    <p className="tw-text-sm tw-text-amber-800 tw-font-medium">
                                        {$t('Warning: one or more budget lines will fall below their committed amount after this change.')}
                                    </p>
                                </div>
                            )}

                            {/* Dependents table */}
                            <div className="tw-overflow-x-auto">
                                <table className="tw-w-full tw-text-sm tw-border-collapse">
                                    <thead>
                                        <tr className="tw-border-b tw-text-left">
                                            <th className="tw-py-2 tw-pr-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide">
                                                {$t('Budget Line')}
                                            </th>
                                            <th className="tw-py-2 tw-px-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                {$t('Current')}
                                            </th>
                                            <th className="tw-py-2 tw-px-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                {$t('New')}
                                            </th>
                                            <th className="tw-py-2 tw-pl-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                {$t('Change')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleDependents.map((dep, i) => (
                                            <tr key={i} className={`tw-border-b tw-border-gray-100 ${dep.over_committed ? 'tw-bg-amber-50' : ''}`}>
                                                <td className="tw-py-2 tw-pr-3 tw-text-charcoal">
                                                    {dep.name}
                                                    {dep.over_committed && (
                                                        <span
                                                            className="tw-ml-1 tw-text-xs tw-text-amber-700 tw-font-medium"
                                                            title={$t('New forecast falls below committed amount')}
                                                        >
                                                            &#9888;
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="tw-py-2 tw-px-3 tw-text-right tw-text-charcoal-75 tw-tabular-nums">
                                                    {formatCurrency(dep.current_forecast)}
                                                </td>
                                                <td className="tw-py-2 tw-px-3 tw-text-right tw-text-charcoal-75 tw-tabular-nums">
                                                    {formatCurrency(dep.new_forecast)}
                                                </td>
                                                <td
                                                    className={`tw-py-2 tw-pl-3 tw-text-right tw-tabular-nums tw-font-medium ${dep.delta_amount > 0 ? 'tw-text-green-700' : dep.delta_amount < 0 ? 'tw-text-red-600' : 'tw-text-charcoal-50'}`}
                                                >
                                                    {formatDelta(dep.delta_amount)}
                                                    {dep.delta_percent !== null && (
                                                        <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-charcoal-50">
                                                            ({formatPercent(dep.delta_percent)})
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {!expanded && preview.dependents.length > SUMMARISE_THRESHOLD && (
                                            <tr>
                                                <td colSpan={4} className="tw-py-2 tw-text-center">
                                                    <button
                                                        type="button"
                                                        className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline"
                                                        onClick={() => setExpanded(true)}
                                                    >
                                                        {$t('Show all :count expense lines', { count: preview.dependents.length })}
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {preview.dependents.length > 1 && (
                                        <tfoot>
                                            <tr className="tw-border-t-2 tw-border-gray-300">
                                                <td className="tw-py-2 tw-pr-3 tw-text-xs tw-font-semibold tw-text-charcoal-75">{$t('Total impact')}</td>
                                                <td colSpan={2}></td>
                                                <td
                                                    className={`tw-py-2 tw-pl-3 tw-text-right tw-tabular-nums tw-font-semibold ${preview.summary.total_delta > 0 ? 'tw-text-green-700' : preview.summary.total_delta < 0 ? 'tw-text-red-600' : 'tw-text-charcoal-50'}`}
                                                >
                                                    {formatDelta(preview.summary.total_delta)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-p-4 tw-border-t tw-flex tw-items-center tw-justify-between">
                    <p className="tw-text-xs tw-text-charcoal-50">{preview ? $t('The linked quantities will update automatically after saving.') : ''}</p>
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={handleCancel}>
                            {$t('Cancel')}
                        </Button>
                        {preview && (
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    window.dispatchEvent(
                                        new CustomEvent('dependentlines:open', {
                                            detail: {
                                                dependentsUrl: state.dependentsUrl,
                                                sourceLabel: state.sourceLabel ?? preview?.source?.label,
                                            },
                                        }),
                                    );
                                }}
                            >
                                {$t('View Dependent Lines')}
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
                            {loading ? $t('Loading...') : $t('Proceed with Update')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const rootEl = document.getElementById('QuantityImpactPreviewApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <QuantityImpactPreviewDialog />
        </App>,
        rootEl,
    );
}
